import { SourceDataVerificationPermission } from '@/api/dtos';
import ConfigurationSummaryList from '@/components/data/configuration/summary/ConfigurationSummaryList';
import Breadcrumbs from '@/components/layouts/Breadcrumbs';
import PageTitle, { TITLE_SEPARATOR } from '@/components/layouts/PageTitle';
import TrialContext from '@/contexts/data/TrialContext';
import useConfigurationSummaries from '@/hooks/data/useConfigurationSummaries';
import usePatient from '@/hooks/data/usePatient';
import usePermissions from '@/hooks/utility/usePermissions';
import { faDna, faGlobeOceania, faPlus, faUser, faUsers } from '@fortawesome/duotone-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Button, Divider, Grid, IconButton, ListItemButton, ListItemText, Paper, styled, Tooltip, Typography, useTheme } from '@mui/material';
import { FC, useContext, useMemo } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';

const Title = styled(Typography)(({ theme }) => ({
    display: 'flex'
}));

const Trial: FC = () => {
    const { trialCode, patientStudyNumber } = useParams();
    
    const {data: trial, loading: trialLoading } = useContext(TrialContext);
    
    const { data: patient, loading: patientLoading, validating: patientUpdating } = usePatient(patientStudyNumber!);

    const theme = useTheme();

    const { 
        data: [
            hasViewPatient
        ] = [], 
        loading: permissionsLoading, 
        validating: permissionsValidating
    } = usePermissions(
        [
            SourceDataVerificationPermission.ViewPatient
        ], 
        patientLoading || trialLoading,
        trial?.id,
        undefined,
        undefined,
        undefined,
        patient?.institutionId,
        patient?.id,
        undefined,
        undefined,
        undefined,
        {
            revalidateOnFocus: false
        }
    );

    const breadcrumbs = useMemo(() => {
        return [
            {
                label: 'Trials',
                link: `/`,
                icon: faDna
            },
            {
                label: 'Participants',
                link: `/${trialCode}/participants`,
                icon: faUsers
            },
            {
                label: patientStudyNumber,
                icon: faUser
            }
        ];
    }, [patientStudyNumber, trialCode]);

    
    if (!hasViewPatient && !permissionsLoading && !permissionsValidating) {
        return (
            <Navigate to='/error/403' />
        )
    }

    return (
        <>
            <PageTitle title={`${patientStudyNumber}${TITLE_SEPARATOR}${trial?.name}`} />
            <Breadcrumbs 
                breadcrumbs={breadcrumbs}
            />
            <Title 
                variant="h1" 
                gutterBottom
                color={theme.palette.secondary.main}
            >
                Participant {patientStudyNumber}
            </Title>
            <Grid 
                container
                spacing={2}
            >
                <Grid 
                    item
                    xs={12}
                    sm={12}
                    md={6}
                    lg={8}
                    xl={9}
                >
                    <Paper>
                        <ListItemButton component={Link} to={`/${trialCode}/participants/${patientStudyNumber}/data`}>
                            <ListItemText
                                primary="Data"
                            />
                        </ListItemButton>
                        <Divider />
                        <ListItemButton component={Link} to={`/${trialCode}/participants/${patientStudyNumber}/discrepancy-notes`}>
                            <ListItemText
                                primary="Discrepancy Notes"
                            />
                        </ListItemButton>
                    </Paper>
                </Grid>
            </Grid>
        </>
    )
}

export default Trial;